import React, { useMemo } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const NON_JSON_LANGS = new Set([
  'javascript',
  'js',
  'typescript',
  'ts',
  'tsx',
  'jsx',
  'python',
  'py',
  'bash',
  'sh',
  'shell',
  'html',
  'css',
  'sql',
]);

function tryParseJson(str: string): unknown | null {
  const trimmed = str.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}

const MarkdownWrap = styled.div`
  font-size: 0.875rem;
  line-height: 1.65;
  word-break: break-word;
  white-space: normal;

  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }

  h1,
  h2,
  h3,
  h4 {
    margin: 0.75em 0 0.35em;
    line-height: 1.3;
    color: var(--text-primary);
  }
  h1 {
    font-size: 1.15em;
  }
  h2 {
    font-size: 1.05em;
  }
  h3 {
    font-size: 0.95em;
  }

  p {
    margin: 0.4em 0;
  }

  ul,
  ol {
    margin: 0.4em 0;
    padding-left: 1.4em;
  }
  li {
    margin: 0.15em 0;
  }
  li > p {
    margin: 0;
  }

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 0.75em 0;
  }

  table {
    border-collapse: collapse;
    margin: 0.5em 0;
    font-size: 0.9em;
    width: 100%;
  }

  th,
  td {
    padding: 0.35em 0.6em;
    border: 1px solid var(--border);
    text-align: left;
  }

  th {
    background: var(--bg-elevated);
    font-weight: 600;
    color: var(--text-primary);
  }
`;

const InlineCode = styled.code`
  font-family: 'SF Mono', 'Fira Code', 'Monaco', monospace;
  font-size: 0.85em;
  padding: 1px 5px;
  background: var(--code-bg, rgba(0, 0, 0, 0.2));
  border-radius: 4px;
`;

const CodeBlockShell = styled.pre`
  margin: 0.5em 0;
  padding: 0.75rem 1rem;
  background: var(--code-bg, rgba(0, 0, 0, 0.2));
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.8125rem;
  line-height: 1.5;

  & code {
    font-family: 'SF Mono', 'Fira Code', 'Monaco', monospace;
    font-size: inherit;
    padding: 0;
    background: none;
    border-radius: 0;
  }
`;

const FormattedJsonPre = styled.pre`
  margin: 0.5em 0;
  padding: 0.75rem 1rem;
  background: var(--code-bg, rgba(0, 0, 0, 0.2));
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'SF Mono', 'Fira Code', 'Monaco', monospace;
  font-size: 0.75rem;
  line-height: 1.55;
  color: var(--text-secondary);
  white-space: pre;
`;

function tryFormatJsonBlock(text: string, lang: string | undefined): string | null {
  const t = text.replace(/\n$/, '').trim();
  if (!t) return null;
  if (lang === 'json') {
    try {
      return JSON.stringify(JSON.parse(text.replace(/\n$/, '')), null, 2);
    } catch {
      return null;
    }
  }
  if (lang && NON_JSON_LANGS.has(lang)) return null;
  if (!(t.startsWith('{') || t.startsWith('['))) return null;
  try {
    return JSON.stringify(JSON.parse(text.replace(/\n$/, '')), null, 2);
  } catch {
    return null;
  }
}

const markdownComponents = (): Components => ({
  /* Fenced blocks are pre>code; we render the block inside `code` (see react-markdown readme). */
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const text = String(children).replace(/\n$/, '');
    if (match) {
      const lang = match[1];
      const formatted = tryFormatJsonBlock(text, lang);
      if (formatted !== null) {
        return <FormattedJsonPre>{formatted}</FormattedJsonPre>;
      }
      return (
        <CodeBlockShell>
          <code className={className} {...props}>
            {children}
          </code>
        </CodeBlockShell>
      );
    }
    /* Fenced block with no language: still wrapped in pre; try JSON if it looks like data. */
    if (text.includes('\n')) {
      const formatted = tryFormatJsonBlock(text, undefined);
      if (formatted !== null) {
        return <FormattedJsonPre>{formatted}</FormattedJsonPre>;
      }
      return (
        <CodeBlockShell>
          <code className={className} {...props}>
            {children}
          </code>
        </CodeBlockShell>
      );
    }
    return <InlineCode {...props}>{children}</InlineCode>;
  },
});

const WorkspaceMessageBody: React.FC<{ content: string }> = ({ content }) => {
  const components = useMemo(() => markdownComponents(), []);

  const pureJson = useMemo(() => {
    const trimmed = content.trim();
    if (!trimmed) return null;
    return tryParseJson(trimmed);
  }, [content]);

  if (pureJson !== null) {
    return (
      <FormattedJsonPre>
        {JSON.stringify(pureJson, null, 2)}
      </FormattedJsonPre>
    );
  }

  return (
    <MarkdownWrap>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </MarkdownWrap>
  );
};

export default WorkspaceMessageBody;
